import React, { useReducer, useEffect } from "react";
import ContentPreview from "../ContentPreview";
import ResultPreview from "./ResultPreview";
import FilterList from "../../utils/FilterList";
import { ContentActionsTypes, ContentType } from "../../../constants";
import AddText from "../content/AddText";
import AddFile from "../content/AddFile";
import { getParameter } from "../../helpers";

export default function ContentSelector({
  content,
  types,
  labelButton,
  filter,
  validator,
  onSelected,
  single,
  Form,
  hideResultsPreview,
  post,
  cancel,
}: {
  content: Content[];
  types: CONTENT_TYPE[];
  labelButton: string;
  filter: Function[];
  validator: Function;
  onSelected: Function;
  single?: boolean;
  hideResultsPreview?: boolean;
  Form: any;
  post: Post;
  cancel?: Function;
}) {
  const [selectedContent, toggleContent] = useReducer(selectorReducer, []);
  const [valid, message] = validator(selectedContent);
  const contentItemsInUse = content
    .filter((c) => types.indexOf(c.type) >= 0 || types.length === 0)
    .filter(
      (c) =>
        !(c.actions || []).find(
          (action) => action === ContentActionsTypes.FILE_MANIPULATION
        )
    )
    .filter((c) => filter.some((filterFunc) => filterFunc(c)));
  const allowTextAddition = filter.find(
    (f) => f === ContentSelector.filterText
  );
  const allowFileAddition = filter.find(
    (f) =>
      f === ContentSelector.filterAudio ||
      f === ContentSelector.filterImage ||
      f === ContentSelector.filterVideo
  );

  useEffect(() => {
    const contentParam = getParameter("content");

    if (contentParam) {
      contentParam.split(",").forEach((contentId) => {
        const content = (post.content || []).find((c) => c.id === contentId);
        if (content) {
          toggleContent(content);
        }
      });
    }
  }, []);

  if (contentItemsInUse.length === 0) {
    return (
      <>
        <p>You don't have the file types required by this feature.</p>
      </>
    );
  }

  return (
    <>
      <ul className="fixed-height-400 content-selector">
        <FilterList
          items={contentItemsInUse.slice().reverse()}
          fields={["meta.name", "value"]}
        >
          {(items) =>
            items.map((c) => {
              const selected = selectedContent.find((sc) => sc.id === c.id);
              const disabled =
                single && selectedContent.length === 1 && !selected;
              return (
                <ContentPreview content={c} key={c.id}>
                  <div
                    className="content-cta-container"
                    style={{ backgroundColor: "var(--main-bg-color)" }}
                  >
                    <button
                      className={
                        "reset as-link ib with-icon mb03 fz12 tac " +
                        (selected ? "activated" : "deactivated")
                      }
                      onClick={() => toggleContent(c)}
                      disabled={disabled}
                    >
                      <small className="fz08">
                        {selected ? "picked" : "pick"}
                      </small>
                    </button>
                  </div>
                </ContentPreview>
              );
            })
          }
        </FilterList>
        <li className="block tac pt05">
          {allowTextAddition && (
            <AddText
              post={post}
              buttonClassName="reset small mr05 pb05"
              noIcon
            />
          )}
          {allowFileAddition && (
            <AddFile post={post} buttonClassName="reset small" noIcon />
          )}
        </li>
      </ul>
      {!hideResultsPreview && (
        <ResultPreview
          selectedContent={selectedContent}
          isSingleFileSelection={!!single}
        />
      )}
      <Form
        valid={valid}
        post={post}
        labelButton={labelButton}
        selectedContent={selectedContent}
        message={message}
        deselect={() => toggleContent(null)}
        cancel={cancel}
        onSubmit={(data) =>
          onSelected({
            content: selectedContent,
            ...data,
          })
        }
      />
    </>
  );
}

function selectorReducer(state, content: Content) {
  if (!content) {
    return [];
  }
  if (state.find((c) => c.id === content.id)) {
    return state.filter((c) => c.id !== content.id);
  }
  return [...state, content];
}
function getContentByType(selectedContent) {
  return [
    selectedContent.filter((c) => (c.meta?.contentType || "").match(/^image/)),
    selectedContent.filter((c) => (c.meta?.contentType || "").match(/^video/)),
    selectedContent.filter((c) => (c.meta?.contentType || "").match(/^audio/)),
  ];
}

/* ***************************** validators */

ContentSelector.audioAndPhoto = (
  selectedContent: Content[]
): [boolean, string] => {
  const [images, videos, audios] = getContentByType(selectedContent);

  if (audios.length === 0) {
    return [false, "Please, select audio file."];
  }
  if (audios.length > 1) {
    return [false, "Please, select exactly one audio file."];
  }
  if (images.length === 0) {
    return [false, "Please, select a photo."];
  }
  if (images.length > 1) {
    return [false, "Please, select exactly one image file."];
  }

  return [true, "Merge the two files"];
};

ContentSelector.audioAndAudio = (
  selectedContent: Content[]
): [boolean, string] => {
  const [images, videos, audios] = getContentByType(selectedContent);

  if (audios.length === 1) {
    return [false, "Please, select more than one audio file."];
  }
  if (audios.length >= 2) {
    return [true, "Merge"];
  }
  return [false, "Please, select two or more audio files."];
};

ContentSelector.singleAudio = (
  selectedContent: Content[]
): [boolean, string] => {
  const [images, videos, audios] = getContentByType(selectedContent);

  if (audios.length === 1) {
    return [true, "Change"];
  }
  return [false, "Please, select an audio file."];
};

ContentSelector.singleVideo = (
  selectedContent: Content[]
): [boolean, string] => {
  const [images, videos, audios] = getContentByType(selectedContent);

  if (videos.length === 1) {
    return [true, "Change"];
  }
  return [false, "Please, select a video file."];
};

ContentSelector.singleImage = (
  selectedContent: Content[]
): [boolean, string] => {
  const [images, videos, audios] = getContentByType(selectedContent);

  if (images.length === 1) {
    return [true, "Change"];
  }
  return [false, "Please, select a photo."];
};

ContentSelector.atLeastOne = (
  selectedContent: Content[]
): [boolean, string] => {
  if (selectedContent.length >= 1) {
    return [true, "Change"];
  }
  return [false, "Please, select one or more files."];
};

/* ***************************** fitters */
ContentSelector.filterAudio = (c: Content) =>
  (c.meta?.contentType || "").match(/^audio/);
ContentSelector.filterVideo = (c: Content) =>
  (c.meta?.contentType || "").match(/^video/);
ContentSelector.filterImage = (c: Content) =>
  (c.meta?.contentType || "").match(/^image/);
ContentSelector.filterText = (c: Content) => c.type === ContentType.TEXT;
ContentSelector.filterAllowAll = [
  ContentSelector.filterAudio,
  ContentSelector.filterVideo,
  ContentSelector.filterImage,
  ContentSelector.filterText,
];
